import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, up } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { DateField } from "@/components/DateField";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEFAULT_SERVICES = [
  { name: "Ανέλκυση σκάφους έως 7μ.", price: 80 },
  { name: "Ανέλκυση 7–10μ.", price: 120 },
  { name: "Ανέλκυση άνω των 10μ.", price: 180 },
  { name: "Καθέλκυση", price: 80 },
  { name: "Πλύσιμο γάστρας (€/μέτρο)", price: 8 },
  { name: "Αντιρρυπαντική βαφή (€/μέτρο)", price: 25 },
  { name: "Γυάλισμα σκάφους (€/μέτρο)", price: 15 },
  { name: "Αλλαγή λαδιών εξωλέμβιας", price: 90 },
  { name: "Service εξωλέμβιας", price: 150 },
  { name: "Service εσωλέμβιας", price: 300 },
  { name: "Φόρτιση μπαταρίας", price: 15 },
  { name: "Φύλαξη τρέιλερ (€/μήνα)", price: 30 },
  { name: "Χρήση νερού", price: 5 },
  { name: "Χρήση ηλεκτρικού ρεύματος (€/ημέρα)", price: 10 },
  { name: "Κάλυψη σκάφους με μουσαμά", price: 50 },
  { name: "Μεταφορά σκάφους εντός εγκατάστασης", price: 30 },
];

export default function Setup({ config }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState(config?.name || "");
  const [businessEmail, setBusinessEmail] = useState(config?.business_email || "");
  const [address, setAddress] = useState(config?.address || "");
  const [city, setCity] = useState(config?.city || "");
  const [postalCode, setPostalCode] = useState(config?.postal_code || "");
  const [phone, setPhone] = useState(config?.phone || "");
  const [taxName, setTaxName] = useState(config?.tax_name || "");
  const [afm, setAfm] = useState(config?.afm || "");
  const [doy, setDoy] = useState(config?.doy || "");
  const [taxAddress, setTaxAddress] = useState(config?.tax_address || "");
  const [autoAlerts, setAutoAlerts] = useState(!!config?.auto_alerts);
  const [periods, setPeriods] = useState(
    config?.periods?.map((p) => ({ name: p.name, start_date: p.start_date || "", end_date: p.end_date || "" })) || [
      { name: "", start_date: "", end_date: "" },
      { name: "", start_date: "", end_date: "" },
    ]
  );
  const [facilityType, setFacilityType] = useState(config?.facility_type || "land");
  const [categories, setCategories] = useState(    config
      ? config.categories.map((c) => ({
          name: c.name,
          min_length: c.min_length,
          max_length: c.max_length,
          slot_count: config.slots.filter((s) => s.category_id === c.id).length,
          yearly: c.yearly ?? 0,
          per_meter: c.per_meter ?? false,
          max_beam: c.max_beam ?? "",
          max_draft: c.max_draft ?? "",
          power: c.power ?? "",
          water: c.water ?? false,
          prices: config.periods.map((p) => {
            const pr = config.prices[`${c.id}|${p.id}`];
            return typeof pr === "object" && pr !== null ? { daily: pr.daily ?? 0, monthly: pr.monthly ?? 0 } : { daily: 0, monthly: pr ?? 0 };
          }),
        }))
      : [
          { name: "Μικρά", min_length: 0, max_length: 8, slot_count: 10, yearly: 1800, prices: [{ daily: 10, monthly: 200 }, { daily: 8, monthly: 150 }] },
          { name: "Μεσαία", min_length: 8, max_length: 15, slot_count: 8, yearly: 3600, prices: [{ daily: 20, monthly: 400 }, { daily: 16, monthly: 300 }] },
          { name: "Μεγάλα", min_length: 15, max_length: 30, slot_count: 5, yearly: 7200, prices: [{ daily: 40, monthly: 800 }, { daily: 32, monthly: 600 }] },
        ]
  );
  const [services, setServices] = useState(
    config?.services?.length
      ? config.services.map((s) => ({ name: s.name, price: s.price ?? 0, enabled: s.enabled !== false }))
      : DEFAULT_SERVICES.map((s) => ({ ...s, enabled: true }))
  );
  const [saving, setSaving] = useState(false);
  const [slotLabels, setSlotLabels] = useState(() => {
    const m = {};
    (config?.slots || []).forEach((s) => { if (s.label) m[String(s.number)] = s.label; });
    return m;
  });

  const setPeriod = (i, key, val) => {
    const next = [...periods];
    next[i] = { ...next[i], [key]: val };
    setPeriods(next);
  };
  const addPeriod = () => {
    setPeriods([...periods, { name: "", start_date: "", end_date: "" }]);
    setCategories(categories.map((c) => ({ ...c, prices: [...c.prices, { daily: 0, monthly: 0 }] })));
  };
  const removePeriod = (i) => {
    setPeriods(periods.filter((_, idx) => idx !== i));
    setCategories(categories.map((c) => ({ ...c, prices: c.prices.filter((_, idx) => idx !== i) })));
  };

  const setCat = (i, key, val) => {
    const next = [...categories];
    next[i] = { ...next[i], [key]: val };
    setCategories(next);
  };
  const setCatPrice = (i, pi, field, val) => {
    const next = [...categories];
    const prices = [...next[i].prices];
    prices[pi] = { ...prices[pi], [field]: val };
    next[i] = { ...next[i], prices };
    setCategories(next);
  };
  const addCat = () => setCategories([...categories, { name: "", min_length: 0, max_length: 0, slot_count: 1, per_meter: false, prices: periods.map(() => ({ daily: 0, monthly: 0, yearly: 0 })) }]);
  const removeCat = (i) => setCategories(categories.filter((_, idx) => idx !== i));

  const setService = (i, key, val) => { const n = [...services]; n[i] = { ...n[i], [key]: val }; setServices(n); };
  const addService = () => setServices([...services, { name: "", price: 0, enabled: true }]);
  const removeService = (i) => setServices(services.filter((_, idx) => idx !== i));

  const setSlotLabel = (num, val) => setSlotLabels((m) => ({ ...m, [num]: val }));
  const slotGroups = (() => {
    let n = 0; const groups = [];
    categories.forEach((c) => {
      const cnt = Math.max(0, Number(c.slot_count) || 0);
      const items = [];
      for (let k = 0; k < cnt; k += 1) { n += 1; items.push(String(n)); }
      groups.push({ name: c.name || "—", items });
    });
    return groups;
  })();

  const save = async () => {
    if (!name.trim()) return toast.error("Συμπληρώστε την επωνυμία");
    if (periods.some((p) => !p.start_date || !p.end_date)) return toast.error("Συμπληρώστε τις ημερομηνίες (Από/Έως) κάθε περιόδου");
    if (periods.some((p) => p.end_date < p.start_date)) return toast.error("Η ημ/νία λήξης δεν μπορεί να είναι πριν την έναρξη");
    // Overlap check (client-side; backend also enforces)
    const ranges = periods.map((p) => [p.start_date, p.end_date]).sort((a, b) => (a[0] < b[0] ? -1 : 1));
    for (let i = 1; i < ranges.length; i++) {
      if (ranges[i][0] <= ranges[i - 1][1]) return toast.error("Οι περίοδοι χρέωσης επικαλύπτονται μεταξύ τους");
    }
    if (categories.some((c) => !c.name.trim())) return toast.error("Συμπληρώστε τα ονόματα κατηγοριών");
    setSaving(true);
    try {
      await api.put("/config", {
        name: name.trim(),
        business_email: businessEmail.trim() || null,
        address: address.trim() || null,
        city: city.trim() || null,
        postal_code: postalCode.trim() || null,
        phone: phone.trim() || null,
        tax_name: taxName.trim() || null,
        afm: afm.trim() || null,
        doy: doy.trim() || null,
        tax_address: taxAddress.trim() || null,
        auto_alerts: autoAlerts,
        facility_type: facilityType,
        periods: periods.map((p) => ({ name: p.name.trim() || null, start_date: p.start_date, end_date: p.end_date })),
        categories: categories.map((c) => ({
          name: c.name.trim(),
          min_length: Number(c.min_length),
          max_length: Number(c.max_length),
          slot_count: Number(c.slot_count),
          yearly: Number(c.yearly || 0),
          per_meter: !!c.per_meter,
          max_beam: c.max_beam === "" || c.max_beam == null ? null : Number(c.max_beam),
          max_draft: c.max_draft === "" || c.max_draft == null ? null : Number(c.max_draft),
          power: (c.power || "").trim() || null,
          water: !!c.water,
          prices: c.prices.map((x) => ({ monthly: Number(x.monthly || 0) })),
        })),
        services: services.filter((s) => s.name.trim()).map((s) => ({ name: s.name.trim(), price: Number(s.price || 0), enabled: !!s.enabled })),
        slot_labels: slotLabels,
      });
      await qc.invalidateQueries();
      toast.success("Αποθηκεύτηκε επιτυχώς");
      navigate("/");
    } catch (e) {
      toast.error("Σφάλμα αποθήκευσης");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-[#234E7D] px-6 py-8 text-white">
        <div className="mx-auto flex max-w-5xl items-center gap-4">
          <img src="/navipark-logo-white.png" alt="NAVIPARK" className="h-auto w-full max-w-[180px]" />
        </div>
      </div>

      <div className="mx-auto max-w-5xl space-y-6 p-6">
        <Card className="rounded-sm border-border shadow-none">
          <div className="border-b border-border px-6 py-4">
            <h2 className="font-head text-lg font-bold">1. Στοιχεία parking</h2>
          </div>
          <div className="p-6">
            <div className="mb-5 flex flex-wrap items-end gap-6">
              <div>
                <Label className="text-xs font-semibold tracking-wider text-muted-foreground">Τύπος εγκατάστασης</Label>
                <Select value={facilityType} onValueChange={setFacilityType}>
                  <SelectTrigger className="mt-2 w-64 rounded-sm" data-testid="setup-facility-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="land">Χερσαία εγκατάσταση</SelectItem>
                    <SelectItem value="sea">Θαλάσσια εγκατάσταση</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {facilityType === "sea"
                    ? "Θέσεις ελλιμενισμού: διαστάσεις (πλάτος/βύθισμα), παροχές και ημερήσιοι ελλιμενισμοί."
                    : "Χερσαία φύλαξη / διαχείμαση σκαφών (όπως έως τώρα)."}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <Label htmlFor="pname" className="text-xs font-semibold tracking-wider text-muted-foreground">Επωνυμία</Label>
                <Input id="pname" data-testid="setup-name" value={name} onChange={(e) => setName(up(e.target.value))} placeholder="π.χ. Marina Alimos" className="mt-2 w-64 rounded-sm" />
              </div>
              <div>
                <Label htmlFor="paddress" className="text-xs font-semibold tracking-wider text-muted-foreground">Διεύθυνση</Label>
                <Input id="paddress" data-testid="setup-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="π.χ. Λεωφ. Ποσειδώνος 10" className="mt-2 w-64 rounded-sm" />
              </div>
              <div>
                <Label htmlFor="pcity" className="text-xs font-semibold tracking-wider text-muted-foreground">Πόλη / Περιοχή</Label>
                <Input id="pcity" data-testid="setup-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="π.χ. Λευκάδα" className="mt-2 w-48 rounded-sm" />
              </div>
              <div>
                <Label htmlFor="ppostal" className="text-xs font-semibold tracking-wider text-muted-foreground">Τ.Κ.</Label>
                <Input id="ppostal" data-testid="setup-postal" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="π.χ. 31100" className="mt-2 w-28 rounded-sm" />
              </div>
              <div>
                <Label htmlFor="pphone" className="text-xs font-semibold tracking-wider text-muted-foreground">Τηλέφωνο</Label>
                <Input id="pphone" data-testid="setup-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="π.χ. 26450 12345" className="mt-2 w-44 rounded-sm" />
              </div>
              <div className="w-full border-t border-dashed border-border pt-4">
                <p className="mb-3 text-xs font-semibold tracking-wider text-muted-foreground">Φορολογικά στοιχεία (για τα συμφωνητικά)</p>
                <div className="flex flex-wrap items-end gap-6">
                  <div>
                    <Label htmlFor="ptaxname" className="text-xs font-semibold tracking-wider text-muted-foreground">Επωνυμία / Υπόχρεος</Label>
                    <Input id="ptaxname" data-testid="setup-tax-name" value={taxName} onChange={(e) => setTaxName(e.target.value)} placeholder="π.χ. Παπαδόπουλος Α. & ΣΙΑ Ο.Ε." className="mt-2 w-64 rounded-sm" />
                  </div>
                  <div>
                    <Label htmlFor="pafm" className="text-xs font-semibold tracking-wider text-muted-foreground">Α.Φ.Μ.</Label>
                    <Input id="pafm" data-testid="setup-afm" value={afm} onChange={(e) => setAfm(e.target.value)} placeholder="π.χ. 099999999" className="mt-2 w-40 rounded-sm" />
                  </div>
                  <div>
                    <Label htmlFor="pdoy" className="text-xs font-semibold tracking-wider text-muted-foreground">Δ.Ο.Υ.</Label>
                    <Input id="pdoy" data-testid="setup-doy" value={doy} onChange={(e) => setDoy(e.target.value)} placeholder="π.χ. Λευκάδας" className="mt-2 w-40 rounded-sm" />
                  </div>
                  <div>
                    <Label htmlFor="ptaxaddr" className="text-xs font-semibold tracking-wider text-muted-foreground">Έδρα (τιμολόγησης)</Label>
                    <Input id="ptaxaddr" data-testid="setup-tax-address" value={taxAddress} onChange={(e) => setTaxAddress(e.target.value)} placeholder="π.χ. Λευκάδα 31100" className="mt-2 w-64 rounded-sm" />
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="bemail" className="text-xs font-semibold tracking-wider text-muted-foreground">Email επιχείρησης (επικοινωνία)</Label>
                <Input id="bemail" type="email" data-testid="setup-business-email" value={businessEmail} onChange={(e) => setBusinessEmail(e.target.value)} placeholder="π.χ. info@marina.gr" className="mt-2 w-64 rounded-sm" />
              </div>
              <label className="flex items-center gap-2 pb-2 text-sm">
                <Checkbox checked={autoAlerts} onCheckedChange={(v) => setAutoAlerts(!!v)} data-testid="setup-auto-alerts" />
                Αποστολή αυτόματων μηνυμάτων προειδοποίησης
              </label>
            </div>
          </div>
        </Card>

        <Card className="rounded-sm border-border shadow-none">
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <h2 className="font-head text-lg font-bold">2. Περίοδοι χρέωσης</h2>
            <Button size="sm" variant="outline" onClick={addPeriod} data-testid="add-period-btn" className="rounded-sm">
              <Plus size={16} weight="bold" /> Περίοδος
            </Button>
          </div>
          <div className="space-y-3 p-6">
            {periods.map((p, i) => (
              <div key={i} className="rounded-sm border border-border p-3" data-testid={`period-row-${i}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-head text-sm font-bold text-[#234E7D]">Περίοδος {i + 1}</span>
                  <Button variant="ghost" size="icon" onClick={() => removePeriod(i)} disabled={periods.length <= 1} className="h-8 w-8 rounded-sm text-destructive" data-testid={`remove-period-${i}`}>
                    <Trash size={16} />
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Ονομασία (προαιρετικό)</Label>
                    <Input value={p.name} onChange={(e) => setPeriod(i, "name", up(e.target.value))} placeholder={`Περίοδος ${i + 1}`} className="mt-1 rounded-sm" data-testid={`period-name-${i}`} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Από</Label>
                    <DateField value={p.start_date} onChange={(v) => setPeriod(i, "start_date", v)} testId={`period-start-${i}`} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Έως</Label>
                    <DateField value={p.end_date} onChange={(v) => setPeriod(i, "end_date", v)} testId={`period-end-${i}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-sm border-border shadow-none">
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div>
              <h2 className="font-head text-lg font-bold">3. Κατηγορίες θέσεων & τιμές</h2>              <p className="text-xs text-muted-foreground">Ορίστε εύρη μήκους, αριθμό θέσεων και τιμή ανά περίοδο.</p>
            </div>
            <Button size="sm" variant="outline" onClick={addCat} data-testid="add-category-btn" className="rounded-sm">
              <Plus size={16} weight="bold" /> Κατηγορία
            </Button>
          </div>
          <div className="space-y-4 p-6">
            {categories.map((c, i) => (
              <div key={i} className="rounded-sm border border-border p-4" data-testid={`category-row-${i}`}>
                <div className="flex items-start justify-between">
                  <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-5">
                    <div>
                      <Label className="text-xs text-muted-foreground">Όνομα</Label>
                      <Input value={c.name} onChange={(e) => setCat(i, "name", up(e.target.value))} className="mt-1 rounded-sm" data-testid={`category-name-${i}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Μήκος από (m)</Label>
                      <Input type="number" value={c.min_length} onChange={(e) => setCat(i, "min_length", e.target.value)} className="mt-1 rounded-sm" data-testid={`category-min-${i}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Μήκος έως (m)</Label>
                      <Input type="number" value={c.max_length} onChange={(e) => setCat(i, "max_length", e.target.value)} className="mt-1 rounded-sm" data-testid={`category-max-${i}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Θέσεις</Label>
                      <Input type="number" value={c.slot_count} onChange={(e) => setCat(i, "slot_count", e.target.value)} className="mt-1 rounded-sm" data-testid={`category-slots-${i}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Ετήσια χρέωση ({c.per_meter ? "€/μ" : "€"})</Label>
                      <Input type="number" value={c.yearly ?? 0} onChange={(e) => setCat(i, "yearly", e.target.value)} className="mt-1 rounded-sm" data-testid={`category-yearly-${i}`} />
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeCat(i)} disabled={categories.length <= 1} className="ml-2 rounded-sm text-destructive" data-testid={`remove-category-${i}`}>
                    <Trash size={18} />
                  </Button>
                </div>
                <label className="mt-3 flex items-center gap-2 text-sm" data-testid={`category-permeter-label-${i}`}>
                  <Checkbox checked={!!c.per_meter} onCheckedChange={(v) => setCat(i, "per_meter", !!v)} data-testid={`category-permeter-${i}`} />
                  Χρέωση ανά μέτρο σκάφους — οι τιμές θεωρούνται €/μέτρο (× μήκος σκάφους)
                </label>
                {facilityType === "sea" && (
                  <div className="mt-3 grid grid-cols-2 gap-3 rounded-sm bg-secondary/40 p-3 sm:grid-cols-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Μέγιστο πλάτος (m)</Label>
                      <Input type="number" step="0.1" value={c.max_beam ?? ""} onChange={(e) => setCat(i, "max_beam", e.target.value)} className="mt-1 rounded-sm" data-testid={`category-beam-${i}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Μέγιστο βύθισμα (m)</Label>
                      <Input type="number" step="0.1" value={c.max_draft ?? ""} onChange={(e) => setCat(i, "max_draft", e.target.value)} className="mt-1 rounded-sm" data-testid={`category-draft-${i}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Ρεύμα (A)</Label>
                      <Input value={c.power ?? ""} onChange={(e) => setCat(i, "power", e.target.value)} placeholder="16 / 32 / 63" className="mt-1 rounded-sm" data-testid={`category-power-${i}`} />
                    </div>
                    <label className="flex items-end gap-2 pb-2 text-sm">
                      <Checkbox checked={!!c.water} onCheckedChange={(v) => setCat(i, "water", !!v)} data-testid={`category-water-${i}`} />
                      Παροχή νερού
                    </label>
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-3">
                  {periods.map((p, pi) => (
                    <div key={pi} className="min-w-[160px] flex-1 rounded-sm bg-secondary/50 p-3">
                      <div className="mb-2 text-xs font-semibold text-[#234E7D]">{p.name || `Περίοδος ${pi + 1}`}</div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Μηνιαία χρέωση ({c.per_meter ? "€/μ" : "€"})</Label>
                        <Input type="number" value={c.prices[pi]?.monthly ?? 0} onChange={(e) => setCatPrice(i, pi, "monthly", e.target.value)} className="mt-1 rounded-sm" data-testid={`category-price-monthly-${i}-${pi}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-sm border-border shadow-none">
          <div className="border-b border-border px-6 py-4">
            <h2 className="font-head text-lg font-bold">4. Ονομασία θέσεων (προαιρετικό)</h2>
            <p className="text-xs text-muted-foreground">Δώστε σε κάθε θέση δικό σας όνομα. Αν το αφήσετε κενό, θα εμφανίζεται ο αριθμός της θέσης.</p>
          </div>
          <div className="space-y-5 p-6" data-testid="slot-labels-section">
            {slotGroups.every((g) => g.items.length === 0) ? (
              <div className="text-sm text-muted-foreground">Ορίστε πρώτα το πλήθος θέσεων στις κατηγορίες.</div>
            ) : (
              slotGroups.map((g, gi) => (
                <div key={gi}>
                  <div className="mb-2 text-sm font-semibold text-[#234E7D]">{g.name}</div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                    {g.items.map((num) => (
                      <div key={num} className="flex items-center gap-2">
                        <span className="w-7 shrink-0 text-xs text-muted-foreground tabular-nums">#{num}</span>
                        <Input value={slotLabels[num] || ""} onChange={(e) => setSlotLabel(num, e.target.value)} placeholder={`Θέση ${num}`} className="rounded-sm" data-testid={`slot-label-${num}`} />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card className="rounded-sm border-border shadow-none">
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div>
              <h2 className="font-head text-lg font-bold">5. Παρεχόμενες υπηρεσίες</h2>
              <p className="text-xs text-muted-foreground">Επιλέξτε τις υπηρεσίες και ορίστε προτεινόμενη τιμή.</p>
            </div>
            <Button size="sm" variant="outline" onClick={addService} data-testid="add-service-btn" className="rounded-sm">
              <Plus size={16} weight="bold" /> Υπηρεσία
            </Button>
          </div>
          <div className="space-y-2 p-6">
            {services.map((s, i) => (
              <div key={i} className="flex items-center gap-3 rounded-sm border border-border p-2" data-testid={`service-row-${i}`}>
                <Checkbox checked={s.enabled} onCheckedChange={(v) => setService(i, "enabled", !!v)} data-testid={`service-enabled-${i}`} />
                <Input value={s.name} onChange={(e) => setService(i, "name", e.target.value)} className="flex-1 rounded-sm" data-testid={`service-name-${i}`} />
                <div className="flex items-center gap-1">
                  <Input type="number" value={s.price} onChange={(e) => setService(i, "price", e.target.value)} className="w-28 rounded-sm" data-testid={`service-price-${i}`} />
                  <span className="text-xs text-muted-foreground">€</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeService(i)} className="rounded-sm text-destructive" data-testid={`remove-service-${i}`}>
                  <Trash size={16} />
                </Button>
              </div>
            ))}
          </div>
        </Card>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="setup-save-btn" className="rounded-sm bg-[#0055FF] px-8 hover:bg-[#0044CC]">
            {saving ? "Αποθήκευση..." : "Αποθήκευση & συνέχεια"}
          </Button>
        </div>
      </div>
    </div>
  );
}
